import {
  escapeHTML,
  formatUSDC,
  parseJSON,
  shortValue,
  showStatus,
  usdcToAtomic,
} from "./ui-utils";
import {
  inferParametersFromExample,
  inferResponseDefinition,
  parseJSONObject,
  parseParameterDefinitions,
  parseResponseDefinition,
  templateFromParameters,
  validateParameterDefinitions,
  validateResponseDefinition,
  type ParameterDefinition,
  type ParameterOption,
  type ParameterType,
  type ResponseDisplayDefinition,
  type ResponseFieldDefinition,
} from "./product-contract";
import { calculateFeePreview } from "./fee-preview";

type ProductStatus = "DRAFT" | "PENDING_REVIEW" | "PUBLISHED" | "ARCHIVED";

type Product = {
  id: string;
  providerId: string;
  productType: "ON_DEMAND" | "NOTIFICATION";
  slug: string;
  name: string;
  description: string;
  serviceUrl: string;
  method: "GET" | "POST";
  providerType: "GENERIC" | "WINSHIELD";
  providerAction: string;
  requestEncoding: "JSON" | "QUERY" | "MULTIPART";
  requestSchema: string;
  requestTemplate: string;
  responseSchema: string;
  paymentMode: "EXACT" | "BATCH";
  network: string;
  tokenAddress: string;
  priceUsdcAtomic: string;
  costUsdcAtomic?: string;
  providerInternalCostUsdcAtomic?: string;
  receiverWallet: string;
  notification?: {
    activationUrl: string;
    terminationUrl: string;
    includedUsageCount: number;
    usageUnitAmountAtomic: string;
    hasSigningSecret: boolean;
  };
  status: ProductStatus;
  version: number;
  updatedAt: string;
  workingVersion?: ProductVersion;
};

type ProductVersion = {
  id: string;
  productId: string;
  baseVersion: number;
  version: number;
  status: ProductStatus;
  definition: Product;
  submittedAt?: string;
  updatedAt: string;
};

type ProductProviderBinding = {
  productId: string;
  environment: string;
  alias: string;
  endpointUrl: string;
  enabled: boolean;
  active: boolean;
  hasUsername: boolean;
  hasPassword: boolean;
  hasSecret: boolean;
  version: number;
  status: string;
  lastTestStatus: string;
};

type NotificationSubscription = {
  id: string;
  productId: string;
  productName: string;
  payerWallet: string;
  status: string;
  quotaTotal: number;
  quotaRemaining: number;
  balanceTotalAtomic: string;
  balanceRemainingAtomic: string;
  terminationReason?: string;
  activationCallbackStatus?: string;
  terminationCallbackStatus?: string;
  updatedAt: string;
};

type ProviderAPI = <T>(path: string, options?: RequestInit) => Promise<T>;

type FeeRule = {
  scope: "DEFAULT" | "PROVIDER";
  feeBps: number;
  version: number;
};

export function initializeProductConsole(api: ProviderAPI): { loadProducts: () => Promise<void>; setFeeRule: (rule: FeeRule) => void } {
  let products: Product[] = [];
  let notificationSubscriptions: NotificationSubscription[] = [];
  let currentBinding: ProductProviderBinding | undefined;
  let wizardStep = 1;
  let requestTemplateFallback: Record<string, unknown> = {};
  let requestAdvancedDirty = false;
  let responseAdvancedDirty = false;
  let lastSimulationResult: unknown;
  let feeRule: FeeRule | undefined;

  const productForm = document.querySelector<HTMLFormElement>("#product-form")!;
  const productRows = document.querySelector<HTMLElement>("#product-rows")!;
  const productDialog = document.querySelector<HTMLDialogElement>("#product-dialog")!;
  const parameterRows = document.querySelector<HTMLElement>("#parameter-rows")!;
  const responseFieldRows = document.querySelector<HTMLElement>("#response-field-rows")!;
  const responseSummaryFieldRows = document.querySelector<HTMLElement>("#response-summary-field-rows")!;

  function editableDefinition(product: Product): Product {
    return product.workingVersion?.definition || product;
  }

  function canEditProduct(product: Product): boolean {
    return product.status === "DRAFT" ||
      (product.status === "PUBLISHED" && product.workingVersion?.status !== "PENDING_REVIEW");
  }

  function canSubmitProduct(product: Product): boolean {
    return product.status === "DRAFT" || product.workingVersion?.status === "DRAFT";
  }

  function renderProducts(): void {
    document.querySelector<HTMLElement>("#product-empty")!.hidden = products.length !== 0;
    document.querySelector("#summary-count")!.textContent = `${products.length} SERVICES`;
    productRows.innerHTML = products.map(product => {
      const working = product.workingVersion;
      const versionLabel = product.status === "PUBLISHED" ? `线上 V${product.version}` : `V${product.version}`;
      const editLabel = product.status === "DRAFT"
        ? "查看 / 编辑"
        : working?.status === "PENDING_REVIEW"
          ? `查看待审 V${working.version}`
          : working?.status === "DRAFT"
            ? `继续编辑 V${working.version}`
            : product.status === "PUBLISHED"
              ? "编辑下一版本"
              : "查看";
      const preview = calculateFeePreview(product.priceUsdcAtomic, feeRule?.feeBps || 0);
      const internalCost = product.providerInternalCostUsdcAtomic;
      return `
        <tr>
          <td><strong>${escapeHTML(product.name)}</strong><small>${escapeHTML(product.slug)} · ${product.productType === "NOTIFICATION" ? "NOTIFICATION" : `${escapeHTML(product.providerType)}${product.providerAction ? ` / ${escapeHTML(product.providerAction)}` : ""}`} · ${versionLabel}</small></td>
          <td><strong>${escapeHTML(formatUSDC(product.priceUsdcAtomic))} USDC</strong><small>预计到手 ${escapeHTML(formatUSDC(preview.payableAtomic))} · 内部成本 ${internalCost ? escapeHTML(formatUSDC(internalCost)) : "未填写"}</small></td>
          <td><span class="status-chip ${product.status.toLowerCase()}">${escapeHTML(product.status)}</span>${working ? `<small>下一版本 V${working.version} · ${escapeHTML(working.status)}</small>` : ""}</td>
          <td class="row-actions"><button type="button" data-edit="${escapeHTML(product.id)}">${editLabel}</button>${canSubmitProduct(product) ? `<button type="button" data-submit="${escapeHTML(product.id)}">提交${working ? ` V${working.version}` : ""}审核</button>` : ""}</td>
        </tr>
      `;
    }).join("");
    productRows.querySelectorAll<HTMLButtonElement>("[data-edit]").forEach(button => {
      button.addEventListener("click", () => void editProduct(button.dataset.edit!));
    });
    productRows.querySelectorAll<HTMLButtonElement>("[data-submit]").forEach(button => {
      button.addEventListener("click", () => void submitProduct(button.dataset.submit!));
    });
  }

  function updateFeePreview(): void {
    const priceValue = (document.querySelector<HTMLInputElement>("#product-price")!).value;
    const costValue = (document.querySelector<HTMLInputElement>("#product-cost")!).value;
    const bps = feeRule?.feeBps ?? 0;
    document.querySelector("#product-fee-rate")!.textContent = feeRule ? `${bps} BPS · ${(bps / 100).toFixed(2)}%` : "加载中";
    document.querySelector("#product-fee-source")!.textContent = feeRule
      ? `${feeRule.scope === "PROVIDER" ? "服务商单独费率" : "平台全局默认"} · V${feeRule.version}`
      : "只读，由平台配置";
    try {
      const preview = calculateFeePreview(
        usdcToAtomic(priceValue),
        bps,
        costValue.trim() ? usdcToAtomic(costValue) : undefined,
      );
      document.querySelector("#product-fee-amount")!.textContent = `${formatUSDC(preview.feeAtomic)} USDC`;
      document.querySelector("#product-payable-amount")!.textContent = `${formatUSDC(preview.payableAtomic)} USDC`;
      if (preview.profitAtomic !== undefined) {
        const negative = preview.profitAtomic.startsWith("-");
        document.querySelector("#product-profit-amount")!.textContent = `${negative ? "-" : ""}${formatUSDC(negative ? preview.profitAtomic.slice(1) : preview.profitAtomic)} USDC`;
      } else {
        document.querySelector("#product-profit-amount")!.textContent = "未填写成本";
      }
    } catch {
      document.querySelector("#product-fee-amount")!.textContent = "—";
      document.querySelector("#product-payable-amount")!.textContent = "—";
      document.querySelector("#product-profit-amount")!.textContent = costValue.trim() ? "—" : "未填写成本";
    }
  }

  function renderNotificationSubscriptions(): void {
    const panel = document.querySelector<HTMLElement>("#notification-subscription-panel")!;
    const rows = document.querySelector<HTMLElement>("#notification-subscription-rows")!;
    panel.hidden = !products.some(product => product.productType === "NOTIFICATION");
    rows.innerHTML = notificationSubscriptions.map(subscription => `
      <tr>
        <td><strong>${escapeHTML(shortValue(subscription.id))}</strong><small>${escapeHTML(subscription.productName)} · ${escapeHTML(shortValue(subscription.payerWallet))}</small></td>
        <td><strong>${subscription.quotaRemaining} / ${subscription.quotaTotal}</strong></td>
        <td><strong>${escapeHTML(formatUSDC(subscription.balanceRemainingAtomic))} USDC</strong><small>套餐 ${escapeHTML(formatUSDC(subscription.balanceTotalAtomic))} USDC</small></td>
        <td><span class="status-chip ${subscription.status.toLowerCase()}">${escapeHTML(subscription.status)}</span><small>${escapeHTML(subscription.terminationReason || new Date(subscription.updatedAt).toLocaleString("zh-CN"))}</small></td>
        <td><small>开通 ${escapeHTML(subscription.activationCallbackStatus || "—")}</small><small>终止 ${escapeHTML(subscription.terminationCallbackStatus || "—")}</small></td>
      </tr>
    `).join("");
    document.querySelector<HTMLElement>("#notification-subscription-empty")!.hidden = notificationSubscriptions.length !== 0;
  }

  async function loadNotificationSubscriptions(): Promise<void> {
    const body = await api<{ subscriptions: NotificationSubscription[] }>("/v1/provider/notification-subscriptions");
    notificationSubscriptions = body.subscriptions;
    renderNotificationSubscriptions();
  }

  async function loadProducts(): Promise<void> {
    const body = await api<{ products: Product[] }>("/v1/provider/products");
    products = body.products;
    renderProducts();
    await loadNotificationSubscriptions();
  }

  const parameterTypes: Array<[ParameterType, string]> = [
    ["string", "单行文本"], ["textarea", "多行文本"], ["integer", "整数"],
    ["number", "数字"], ["boolean", "布尔开关"], ["select", "下拉选择"],
    ["chain", "区块链"], ["date", "日期"], ["datetime", "日期时间"],
    ["file", "文件"], ["json", "JSON"], ["array", "重复列表"],
  ];
  const arrayItemTypes: Array<[ParameterType, string]> = parameterTypes.filter(([type]) =>
    ["string", "integer", "number", "boolean", "select", "chain", "date", "datetime"].includes(type),
  );
  const commonChainOptions: ParameterOption[] = [
    { label: "Ethereum", value: "1" },
    { label: "Base", value: "8453" },
    { label: "Base Sepolia", value: "84532" },
    { label: "Polygon", value: "137" },
  ];

  function typeOptions(types: Array<[ParameterType, string]>, selected: ParameterType): string {
    return types.map(([value, label]) =>
      `<option value="${value}"${selected === value ? " selected" : ""}>${label}</option>`,
    ).join("");
  }

  function responseFormatOptions(selected: ResponseFieldDefinition["format"]): string {
    const labels: Array<[ResponseFieldDefinition["format"], string]> = [
      ["text", "文本"], ["number", "数字"], ["boolean", "是 / 否"],
      ["datetime", "日期时间"], ["link", "链接"], ["json", "JSON"],
    ];
    return labels.map(([value, label]) =>
      `<option value="${value}"${selected === value ? " selected" : ""}>${label}</option>`,
    ).join("");
  }

  function formatOptions(options: ParameterOption[]): string {
    return options.map(option => option.label === option.value
      ? option.value
      : `${option.label}:${option.value}`).join(", ");
  }

  function parseOptions(value: string): ParameterOption[] {
    return value.split(",").map(item => item.trim()).filter(Boolean).map(item => {
      const separator = item.indexOf(":");
      return separator < 0
        ? { label: item, value: item }
        : { label: item.slice(0, separator).trim(), value: item.slice(separator + 1).trim() };
    });
  }

  function optionalNumber(value: string): number | undefined {
    if (value.trim() === "") return undefined;
    const number = Number(value);
    return Number.isFinite(number) ? number : undefined;
  }

  function updateParameterCard(row: HTMLElement): void {
    const type = row.querySelector<HTMLSelectElement>("[data-param=type]")!.value as ParameterType;
    const itemType = row.querySelector<HTMLSelectElement>("[data-rule=itemType]")!.value as ParameterType;
    const options = row.querySelector<HTMLInputElement>("[data-rule=options]")!;
    const itemOptions = row.querySelector<HTMLInputElement>("[data-rule=itemOptions]")!;
    if (type === "chain" && options.value.trim() === "") options.value = formatOptions(commonChainOptions);
    if (itemType === "chain" && itemOptions.value.trim() === "") itemOptions.value = formatOptions(commonChainOptions);
    row.querySelectorAll<HTMLElement>(".option-field").forEach(element => {
      element.hidden = type !== "select" && type !== "chain";
    });
    row.querySelectorAll<HTMLElement>(".file-field").forEach(element => {
      element.hidden = type !== "file";
    });
    row.querySelectorAll<HTMLElement>(".string-field").forEach(element => {
      element.hidden = type !== "string" && type !== "textarea";
    });
    row.querySelectorAll<HTMLElement>(".array-field").forEach(element => {
      element.hidden = type !== "array";
    });
    row.querySelectorAll<HTMLElement>(".item-option-field").forEach(element => {
      element.hidden = type !== "array" || (itemType !== "select" && itemType !== "chain");
    });
    row.querySelectorAll<HTMLElement>(".item-string-field").forEach(element => {
      element.hidden = type !== "array" || itemType !== "string";
    });
    const defaultInput = row.querySelector<HTMLInputElement>("[data-param=defaultValue]")!;
    defaultInput.placeholder = type === "array" ? '["example"]' : type === "json" ? '{"key":"value"}' : "";
    if (type === "file") {
      (document.querySelector<HTMLSelectElement>("#product-request-encoding")!).value = "MULTIPART";
      (document.querySelector<HTMLSelectElement>("#product-method")!).value = "POST";
    }
  }

  function renderParameterDefinitions(definitions: ParameterDefinition[]): void {
    parameterRows.innerHTML = definitions.map((item, index) => {
      const arrayItem = item.items || {
        name: "item", label: "每项", type: "string", required: false,
      } satisfies ParameterDefinition;
      return `
        <div class="definition-card" data-parameter-row>
          <div class="definition-number">${String(index + 1).padStart(2, "0")}</div>
          <div class="definition-fields">
            <label class="field"><span>参数名</span><input data-param="name" value="${escapeHTML(item.name || "")}" placeholder="例如 email" required /></label>
            <label class="field"><span>买方看到的名称</span><input data-param="label" value="${escapeHTML(item.label || item.name || "")}" placeholder="例如 通知邮箱" required /></label>
            <label class="field"><span>参数类型</span><select data-param="type">${typeOptions(parameterTypes, item.type || "string")}</select></label>
            <label class="check-field"><input data-param="required" type="checkbox"${item.required ? " checked" : ""} /><span>必填</span></label>
            <label class="field"><span>示例 / 默认值</span><input data-param="defaultValue" value="${escapeHTML(item.defaultValue || "")}" /></label>
            <label class="field"><span>输入提示</span><input data-param="placeholder" value="${escapeHTML(item.placeholder || "")}" /></label>
            <label class="field wide-field"><span>帮助说明</span><input data-param="helpText" value="${escapeHTML(item.helpText || "")}" /></label>
            <details class="definition-rules">
              <summary>更多校验规则</summary>
              <div class="definition-rule-grid">
                <label class="field option-field"><span>选项（名称:值，逗号分隔）</span><input data-rule="options" value="${escapeHTML(formatOptions(item.options || []))}" placeholder="低风险:LOW, 高风险:HIGH" /></label>
                <label class="field file-field"><span>允许文件类型</span><input data-rule="accept" value="${escapeHTML(item.accept || "")}" placeholder=".pdf,image/*" /></label>
                <label class="field file-field"><span>最大文件字节</span><input data-rule="maxBytes" type="number" min="1" max="8388608" value="${item.maxBytes || 8388608}" /></label>
                <label class="field string-field"><span>文本格式</span><select data-rule="format"><option value="">普通文本</option><option value="email"${item.format === "email" ? " selected" : ""}>Email</option><option value="uri"${item.format === "uri" ? " selected" : ""}>URL</option></select></label>
                <label class="field string-field"><span>最小长度</span><input data-rule="minLength" type="number" min="0" value="${item.minLength || ""}" /></label>
                <label class="field string-field"><span>最大长度</span><input data-rule="maxLength" type="number" min="0" value="${item.maxLength || ""}" /></label>
                <label class="field string-field wide-field"><span>正则表达式</span><input data-rule="pattern" value="${escapeHTML(item.pattern || "")}" placeholder="例如 ^[A-Z0-9]+$" /></label>
                <label class="field array-field"><span>每项类型</span><select data-rule="itemType">${typeOptions(arrayItemTypes, arrayItem.type || "string")}</select></label>
                <label class="field array-field"><span>最少项数</span><input data-rule="minItems" type="number" min="0" max="100" value="${item.minItems || ""}" /></label>
                <label class="field array-field"><span>最多项数</span><input data-rule="maxItems" type="number" min="1" max="100" value="${item.maxItems || 20}" /></label>
                <label class="check-field array-field"><input data-rule="uniqueItems" type="checkbox"${item.uniqueItems ? " checked" : ""} /><span>不允许重复项</span></label>
                <label class="field item-option-field wide-field"><span>数组项选项（名称:值，逗号分隔）</span><input data-rule="itemOptions" value="${escapeHTML(formatOptions(arrayItem.options || []))}" /></label>
                <label class="field item-string-field"><span>数组项文本格式</span><select data-rule="itemFormat"><option value="">普通文本</option><option value="email"${arrayItem.format === "email" ? " selected" : ""}>Email</option><option value="uri"${arrayItem.format === "uri" ? " selected" : ""}>URL</option></select></label>
                <label class="field item-string-field"><span>数组项最小长度</span><input data-rule="itemMinLength" type="number" min="0" value="${arrayItem.minLength || ""}" /></label>
                <label class="field item-string-field"><span>数组项最大长度</span><input data-rule="itemMaxLength" type="number" min="0" value="${arrayItem.maxLength || ""}" /></label>
                <label class="field item-string-field wide-field"><span>数组项正则表达式</span><input data-rule="itemPattern" value="${escapeHTML(arrayItem.pattern || "")}" /></label>
              </div>
            </details>
          </div>
          <button class="remove-definition" data-remove-parameter type="button">删除</button>
        </div>
      `;
    }).join("");
    document.querySelector<HTMLElement>("#parameter-empty")!.hidden = definitions.length !== 0;
    parameterRows.querySelectorAll<HTMLElement>("[data-parameter-row]").forEach(updateParameterCard);
    parameterRows.querySelectorAll<HTMLButtonElement>("[data-remove-parameter]").forEach(button => {
      button.addEventListener("click", () => {
        button.closest("[data-parameter-row]")?.remove();
        renumberDefinitions(parameterRows);
        const hasRows = parameterRows.querySelector("[data-parameter-row]") !== null;
        document.querySelector<HTMLElement>("#parameter-empty")!.hidden = hasRows;
        if (!hasRows) requestTemplateFallback = {};
        syncRequestContract();
      });
    });
  }

  function renumberDefinitions(container: HTMLElement): void {
    container.querySelectorAll<HTMLElement>(".definition-number").forEach((element, index) => {
      element.textContent = String(index + 1).padStart(2, "0");
    });
  }

  function readParameterDefinitions(): ParameterDefinition[] {
    return Array.from(parameterRows.querySelectorAll<HTMLElement>("[data-parameter-row]")).map(row => {
      const inputValue = (selector: string) => row.querySelector<HTMLInputElement>(selector)?.value.trim() || "";
      const selectValue = (selector: string) => row.querySelector<HTMLSelectElement>(selector)?.value || "";
      const type = selectValue("[data-param=type]") as ParameterType;
      const definition: ParameterDefinition = {
        name: inputValue("[data-param=name]"),
        label: inputValue("[data-param=label]"),
        type,
        required: row.querySelector<HTMLInputElement>("[data-param=required]")!.checked,
        defaultValue: inputValue("[data-param=defaultValue]") || undefined,
        placeholder: inputValue("[data-param=placeholder]") || undefined,
        helpText: inputValue("[data-param=helpText]") || undefined,
      };
      if (type === "select" || type === "chain") definition.options = parseOptions(inputValue("[data-rule=options]"));
      if (type === "file") {
        definition.accept = inputValue("[data-rule=accept]") || undefined;
        definition.maxBytes = optionalNumber(inputValue("[data-rule=maxBytes]")) || 8388608;
      }
      if (type === "string" || type === "textarea") {
        definition.format = selectValue("[data-rule=format]") as ParameterDefinition["format"] || undefined;
        definition.minLength = optionalNumber(inputValue("[data-rule=minLength]"));
        definition.maxLength = optionalNumber(inputValue("[data-rule=maxLength]"));
        definition.pattern = inputValue("[data-rule=pattern]") || undefined;
      }
      if (type === "array") {
        const itemType = selectValue("[data-rule=itemType]") as ParameterType;
        definition.items = {
          name: "item",
          label: "每项",
          type: itemType,
          required: false,
        };
        if (itemType === "select" || itemType === "chain") {
          definition.items.options = parseOptions(inputValue("[data-rule=itemOptions]"));
        }
        if (itemType === "string") {
          definition.items.format = selectValue("[data-rule=itemFormat]") as ParameterDefinition["format"] || undefined;
          definition.items.minLength = optionalNumber(inputValue("[data-rule=itemMinLength]"));
          definition.items.maxLength = optionalNumber(inputValue("[data-rule=itemMaxLength]"));
          definition.items.pattern = inputValue("[data-rule=itemPattern]") || undefined;
        }
        definition.minItems = optionalNumber(inputValue("[data-rule=minItems]"));
        definition.maxItems = optionalNumber(inputValue("[data-rule=maxItems]")) || 20;
        definition.uniqueItems = row.querySelector<HTMLInputElement>("[data-rule=uniqueItems]")!.checked || undefined;
      }
      return definition;
    });
  }

  function syncRequestContract(): void {
    const definitions = readParameterDefinitions();
    requestTemplateFallback = templateFromParameters(definitions, requestTemplateFallback);
    const schemaField = document.querySelector<HTMLTextAreaElement>("#product-request-schema")!;
    const templateField = document.querySelector<HTMLTextAreaElement>("#product-request-template")!;
    schemaField.value = JSON.stringify(definitions, null, 2);
    templateField.value = JSON.stringify(requestTemplateFallback, null, 2);
    document.querySelector("#request-template-preview")!.textContent = templateField.value;
    requestAdvancedDirty = false;
  }

  function applyAdvancedRequestJSON(showSuccess = true): boolean {
    try {
      const definitions = parseParameterDefinitions(
        document.querySelector<HTMLTextAreaElement>("#product-request-schema")!.value,
      );
      const template = parseJSONObject(
        document.querySelector<HTMLTextAreaElement>("#product-request-template")!.value,
        "Request Template",
      );
      const errors = validateParameterDefinitions(definitions);
      if (errors.length > 0) throw new Error(errors[0]);
      requestTemplateFallback = template;
      renderParameterDefinitions(definitions);
      syncRequestContract();
      (document.querySelector<HTMLTextAreaElement>("#request-example")!).value =
        JSON.stringify(requestTemplateFallback, null, 2);
      if (showSuccess) showStatus("product-status", "高级 JSON 已应用到可视化参数。");
      return true;
    } catch (error) {
      showStatus("product-status", error instanceof Error ? error.message : "输入契约无效", true);
      return false;
    }
  }

  function inferRequestParameters(): void {
    try {
      const example = parseJSONObject(
        document.querySelector<HTMLTextAreaElement>("#request-example")!.value,
        "请求示例",
      );
      const definitions = inferParametersFromExample(example);
      requestTemplateFallback = example;
      renderParameterDefinitions(definitions);
      syncRequestContract();
      showStatus("product-status", `已识别 ${definitions.length} 个请求参数，请检查名称、必填项和校验规则。`);
    } catch (error) {
      showStatus("product-status", error instanceof Error ? error.message : "请求示例无效", true);
    }
  }

  function renderResponseFields(fields: ResponseFieldDefinition[], summary = false): void {
    const container = summary ? responseSummaryFieldRows : responseFieldRows;
    const rowAttribute = summary ? "data-response-summary-field-row" : "data-response-field-row";
    container.innerHTML = fields.map((field, index) => `
      <div class="response-field-row" ${rowAttribute}>
        <span class="definition-number">${String(index + 1).padStart(2, "0")}</span>
        <label class="field"><span>字段路径</span><input data-response="path" value="${escapeHTML(field.path || "")}" placeholder="例如 score 或 rules.0.day" required /></label>
        <label class="field"><span>显示名称</span><input data-response="label" value="${escapeHTML(field.label || "")}" /></label>
        <label class="field"><span>格式</span><select data-response="format">${responseFormatOptions(field.format || "text")}</select></label>
        <button class="remove-definition" data-remove-response type="button">删除</button>
      </div>
    `).join("");
    container.querySelectorAll<HTMLButtonElement>("[data-remove-response]").forEach(button => {
      button.addEventListener("click", () => {
        button.closest(`[${rowAttribute}]`)?.remove();
        renumberDefinitions(container);
        syncResponseContract();
      });
    });
  }

  function readResponseFields(container: HTMLElement, selector: string): ResponseFieldDefinition[] {
    return Array.from(container.querySelectorAll<HTMLElement>(selector)).map(row => ({
      path: row.querySelector<HTMLInputElement>("[data-response=path]")!.value.trim(),
      label: row.querySelector<HTMLInputElement>("[data-response=label]")!.value.trim(),
      format: row.querySelector<HTMLSelectElement>("[data-response=format]")!.value as ResponseFieldDefinition["format"],
    }));
  }

  function readResponseDefinition(): ResponseDisplayDefinition {
    const mode = document.querySelector<HTMLSelectElement>("#response-mode")!.value as ResponseDisplayDefinition["mode"];
    const definition: ResponseDisplayDefinition = {
      mode,
      root: document.querySelector<HTMLInputElement>("#response-root")!.value.trim() || undefined,
    };
    if (["KEY_VALUE", "LIST", "TABLE"].includes(mode)) {
      definition.fields = readResponseFields(responseFieldRows, "[data-response-field-row]");
    }
    if (mode === "LIST") {
      definition.summaryRoot = document.querySelector<HTMLInputElement>("#response-summary-root")!.value.trim() || undefined;
      definition.summaryFields = readResponseFields(responseSummaryFieldRows, "[data-response-summary-field-row]");
    }
    return definition;
  }

  function updateResponseControls(): void {
    const mode = document.querySelector<HTMLSelectElement>("#response-mode")!.value;
    const hasFields = ["KEY_VALUE", "LIST", "TABLE"].includes(mode);
    document.querySelector<HTMLElement>("#response-fields-head")!.hidden = !hasFields;
    responseFieldRows.hidden = !hasFields;
    document.querySelector<HTMLElement>("#response-summary-block")!.hidden = mode !== "LIST";
    responseFieldRows.querySelectorAll<HTMLInputElement | HTMLSelectElement>("input, select").forEach(field => {
      field.disabled = !hasFields;
    });
    responseSummaryFieldRows.querySelectorAll<HTMLInputElement | HTMLSelectElement>("input, select").forEach(field => {
      field.disabled = mode !== "LIST";
    });
  }

  function responseExample(): unknown {
    const raw = document.querySelector<HTMLTextAreaElement>("#response-example")!.value.trim();
    if (!raw) return undefined;
    try {
      return JSON.parse(raw);
    } catch {
      if (raw.startsWith("{") || raw.startsWith("[")) throw new Error("响应示例不是合法 JSON");
      return raw;
    }
  }

  function valueAtPath(source: unknown, path: string): unknown {
    if (!path) return source;
    return path.split(".").reduce<unknown>((current, key) => {
      if (current && typeof current === "object") return (current as Record<string, unknown>)[key];
      return undefined;
    }, source);
  }

  function formatDisplayValue(value: unknown, format: ResponseFieldDefinition["format"]): string {
    if (value === null || value === undefined) return "—";
    if (format === "boolean") return value ? "是" : "否";
    if (format === "datetime") {
      const date = new Date(String(value));
      return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString("zh-CN");
    }
    if (format === "json" || typeof value === "object") return JSON.stringify(value, null, 2);
    return String(value);
  }

  function previewKeyValues(source: unknown, fields: ResponseFieldDefinition[]): string {
    return `<dl class="preview-key-values">${fields.map(field => {
      const value = formatDisplayValue(valueAtPath(source, field.path), field.format);
      return `<div><dt>${escapeHTML(field.label || field.path)}</dt><dd class="${field.format === "link" ? "preview-link" : ""}">${escapeHTML(value)}</dd></div>`;
    }).join("")}</dl>`;
  }

  function renderResponsePreview(): void {
    const preview = document.querySelector<HTMLElement>("#response-preview")!;
    let example: unknown;
    try {
      example = responseExample();
    } catch (error) {
      preview.textContent = error instanceof Error ? error.message : "响应示例无效";
      return;
    }
    if (example === undefined) {
      preview.textContent = "粘贴响应示例后可预览。";
      return;
    }
    const definition = readResponseDefinition();
    const root = valueAtPath(example, definition.root || "");
    if (definition.mode === "JSON") {
      preview.innerHTML = `<pre>${escapeHTML(JSON.stringify(example, null, 2))}</pre>`;
      return;
    }
    if (definition.mode === "TEXT") {
      preview.textContent = formatDisplayValue(root, "text");
      return;
    }
    if (definition.mode === "KEY_VALUE") {
      preview.innerHTML = previewKeyValues(root, definition.fields || []);
      return;
    }
    if (definition.mode === "LIST") {
      const rows = Array.isArray(root) ? root : [];
      const summary = definition.summaryFields?.length
        ? `<section class="preview-group"><header><span>SUMMARY</span><strong>总体结果</strong></header>${previewKeyValues(valueAtPath(example, definition.summaryRoot || ""), definition.summaryFields)}</section>`
        : "";
      preview.innerHTML = rows.length === 0
        ? `${summary}<p>未在根路径提取到数组，请检查数据根路径。</p>`
        : `${summary}${rows.slice(0, 5).map((row, index) => `<section class="preview-group"><header><span>RESULT</span><strong>记录 ${String(index + 1).padStart(2, "0")}</strong></header>${previewKeyValues(row, definition.fields || [])}</section>`).join("")}`;
      return;
    }
    const rows = Array.isArray(root) ? root : [];
    preview.innerHTML = `<div class="preview-table"><table><thead><tr>${(definition.fields || []).map(field => `<th>${escapeHTML(field.label || field.path)}</th>`).join("")}</tr></thead><tbody>${rows.slice(0, 10).map(row => `<tr>${(definition.fields || []).map(field => `<td>${escapeHTML(formatDisplayValue(valueAtPath(row, field.path), field.format))}</td>`).join("")}</tr>`).join("")}</tbody></table></div>`;
  }

  function syncResponseContract(): void {
    updateResponseControls();
    const definition = readResponseDefinition();
    document.querySelector<HTMLTextAreaElement>("#product-response-schema")!.value = JSON.stringify(definition, null, 2);
    responseAdvancedDirty = false;
    renderResponsePreview();
  }

  function applyAdvancedResponseJSON(showSuccess = true): boolean {
    try {
      const definition = parseResponseDefinition(
        document.querySelector<HTMLTextAreaElement>("#product-response-schema")!.value,
      );
      const errors = validateResponseDefinition(definition);
      if (errors.length > 0) throw new Error(errors[0]);
      document.querySelector<HTMLSelectElement>("#response-mode")!.value = definition.mode;
      document.querySelector<HTMLInputElement>("#response-root")!.value = definition.root || "";
      document.querySelector<HTMLInputElement>("#response-summary-root")!.value = definition.summaryRoot || "";
      renderResponseFields(definition.fields || []);
      renderResponseFields(definition.summaryFields || [], true);
      syncResponseContract();
      if (showSuccess) showStatus("product-status", "高级 JSON 已应用到返回展示配置。");
      return true;
    } catch (error) {
      showStatus("product-status", error instanceof Error ? error.message : "返回展示配置无效", true);
      return false;
    }
  }

  function inferResponseDisplay(): void {
    try {
      const example = responseExample();
      if (example === undefined) throw new Error("请先粘贴一份真实响应示例");
      const definition = inferResponseDefinition(example);
      document.querySelector<HTMLSelectElement>("#response-mode")!.value = definition.mode;
      document.querySelector<HTMLInputElement>("#response-root")!.value = definition.root || "";
      document.querySelector<HTMLInputElement>("#response-summary-root")!.value = definition.summaryRoot || "";
      renderResponseFields(definition.fields || []);
      renderResponseFields(definition.summaryFields || [], true);
      syncResponseContract();
      const fieldCount = (definition.fields?.length || 0) + (definition.summaryFields?.length || 0);
      showStatus("product-status", `已生成 ${definition.mode} 展示配置，共识别 ${fieldCount} 个可见字段。`);
    } catch (error) {
      showStatus("product-status", error instanceof Error ? error.message : "响应示例无效", true);
    }
  }

  function setDefinitionLocked(locked: boolean): void {
    const allowed = new Set([
      "close-product-dialog", "wizard-back", "wizard-next", "submit-review", "save-credential", "credential-alias",
      "credential-endpoint", "credential-enabled", "credential-username", "credential-password",
      "credential-secret", "simulation-input", "confirm-provider-charge", "run-simulation",
    ]);
    productForm.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | HTMLButtonElement>(
      "input, textarea, select, button",
    ).forEach(element => {
      if (element.id === "product-id" || allowed.has(element.id)) return;
      element.disabled = locked;
    });
    document.querySelector<HTMLButtonElement>("#save-product")!.hidden = locked;
  }

  function resetProductForm(): void {
    productForm.reset();
    currentBinding = undefined;
    lastSimulationResult = undefined;
    requestTemplateFallback = {};
    requestAdvancedDirty = false;
    responseAdvancedDirty = false;
    setDefinitionLocked(false);
    (document.querySelector<HTMLInputElement>("#product-id")!).value = "";
    (document.querySelector<HTMLSelectElement>("#product-type")!).value = "ON_DEMAND";
    (document.querySelector<HTMLInputElement>("#product-price")!).value = "0.001";
    (document.querySelector<HTMLInputElement>("#product-cost")!).value = "";
    (document.querySelector<HTMLTextAreaElement>("#product-request-schema")!).value = "[]";
    (document.querySelector<HTMLTextAreaElement>("#product-request-template")!).value = "{}";
    (document.querySelector<HTMLTextAreaElement>("#product-response-schema")!).value = '{"mode":"JSON"}';
    (document.querySelector<HTMLTextAreaElement>("#request-example")!).value = "{}";
    (document.querySelector<HTMLTextAreaElement>("#response-example")!).value = "";
    (document.querySelector<HTMLSelectElement>("#response-mode")!).value = "JSON";
    (document.querySelector<HTMLInputElement>("#response-root")!).value = "";
    (document.querySelector<HTMLInputElement>("#response-summary-root")!).value = "";
    (document.querySelector<HTMLTextAreaElement>("#simulation-input")!).value = "{}";
    (document.querySelector<HTMLInputElement>("#notification-included-count")!).value = "100";
    (document.querySelector<HTMLInputElement>("#notification-unit-amount")!).value = "0.01";
    (document.querySelector<HTMLInputElement>("#notification-signing-secret")!).value = "";
    document.querySelector("#editor-title")!.textContent = "NEW SERVICE";
    document.querySelector("#editor-version")!.textContent = "DRAFT";
    document.querySelector<HTMLElement>("#credential-section")!.hidden = true;
    document.querySelector<HTMLElement>("#test-panel")!.hidden = true;
    document.querySelector<HTMLElement>("#submit-review")!.hidden = true;
    document.querySelector<HTMLElement>("#use-simulation-response")!.hidden = true;
    document.querySelector<HTMLElement>("#product-status")!.hidden = true;
    document.querySelector("#simulation-result")!.textContent = "尚未测试";
    renderParameterDefinitions([]);
    renderResponseFields([]);
    renderResponseFields([], true);
    syncRequestContract();
    syncResponseContract();
    setWizardStep(1);
    updateProductTypeFields();
    updateProviderFields();
    updateFeePreview();
  }

  function openProductDialog(): void {
    if (!productDialog.open) productDialog.showModal();
  }

  function closeProductDialog(): void {
    productDialog.close();
  }

  function setWizardStep(step: number): void {
    wizardStep = Math.min(4, Math.max(1, step));
    document.querySelectorAll<HTMLElement>("[data-wizard-step]").forEach(section => {
      section.hidden = Number(section.dataset.wizardStep) !== wizardStep;
    });
    document.querySelectorAll<HTMLElement>("[data-wizard-indicator]").forEach(indicator => {
      const current = Number(indicator.dataset.wizardIndicator);
      indicator.classList.toggle("active", current === wizardStep);
      indicator.classList.toggle("complete", current < wizardStep);
    });
    document.querySelector<HTMLElement>("#wizard-back")!.hidden = wizardStep === 1;
    document.querySelector<HTMLElement>("#wizard-next")!.hidden = wizardStep === 4;
    const save = document.querySelector<HTMLElement>("#save-product")!;
    const currentProduct = products.find(item => item.id === (document.querySelector<HTMLInputElement>("#product-id")!).value);
    save.hidden = wizardStep !== 4 || (currentProduct !== undefined && !canEditProduct(currentProduct));
    document.querySelector("#wizard-position")!.textContent = `${wizardStep} / 4`;
    document.querySelector<HTMLElement>(".wizard-body")!.scrollTop = 0;
  }

  function validateWizardStep(): boolean {
    const section = document.querySelector<HTMLElement>(`[data-wizard-step="${wizardStep}"]`)!;
    for (const field of section.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>("input, textarea, select")) {
      if (!field.disabled && !field.checkValidity()) {
        field.reportValidity();
        return false;
      }
    }
    if (wizardStep === 2) {
      try {
        if (requestAdvancedDirty && !applyAdvancedRequestJSON(false)) return false;
        syncRequestContract();
        const definitions = parseParameterDefinitions(
          document.querySelector<HTMLTextAreaElement>("#product-request-schema")!.value,
        );
        const errors = validateParameterDefinitions(definitions);
        if (errors.length > 0) throw new Error(errors[0]);
        parseJSONObject(
          document.querySelector<HTMLTextAreaElement>("#product-request-template")!.value,
          "Request Template",
        );
        const hasFile = definitions.some(definition => definition.type === "file");
        if (hasFile && document.querySelector<HTMLSelectElement>("#product-request-encoding")!.value !== "MULTIPART") {
          throw new Error("文件参数需要在基本信息中选择 Multipart 请求编码");
        }
      } catch (error) {
        showStatus("product-status", error instanceof Error ? error.message : "输入契约无效", true);
        return false;
      }
    }
    if (wizardStep === 3) {
      try {
        if (responseAdvancedDirty && !applyAdvancedResponseJSON(false)) return false;
        syncResponseContract();
        const responseDefinition = parseResponseDefinition(
          document.querySelector<HTMLTextAreaElement>("#product-response-schema")!.value,
        );
        const errors = validateResponseDefinition(responseDefinition);
        if (errors.length > 0) throw new Error(errors[0]);
        usdcToAtomic((document.querySelector<HTMLInputElement>("#product-price")!).value);
        const internalCost = (document.querySelector<HTMLInputElement>("#product-cost")!).value.trim();
        if (internalCost) usdcToAtomic(internalCost);
      } catch (error) {
        showStatus("product-status", error instanceof Error ? error.message : "定价配置无效", true);
        return false;
      }
    }
    document.querySelector<HTMLElement>("#product-status")!.hidden = true;
    return true;
  }

  async function editProduct(id: string): Promise<void> {
    const product = products.find(item => item.id === id);
    if (!product) return;
    const definition = editableDefinition(product);
    const working = product.workingVersion;
    (document.querySelector<HTMLInputElement>("#product-id")!).value = product.id;
    (document.querySelector<HTMLSelectElement>("#product-type")!).value = definition.productType || "ON_DEMAND";
    (document.querySelector<HTMLInputElement>("#product-name")!).value = definition.name;
    (document.querySelector<HTMLInputElement>("#product-slug")!).value = definition.slug;
    (document.querySelector<HTMLTextAreaElement>("#product-description")!).value = definition.description;
    (document.querySelector<HTMLSelectElement>("#product-provider")!).value = definition.providerType;
    (document.querySelector<HTMLInputElement>("#product-action")!).value = definition.providerAction || "";
    (document.querySelector<HTMLSelectElement>("#product-method")!).value = definition.method;
    (document.querySelector<HTMLInputElement>("#product-url")!).value = definition.serviceUrl;
    (document.querySelector<HTMLSelectElement>("#product-request-encoding")!).value = definition.requestEncoding;
    requestTemplateFallback = parseJSONObject(definition.requestTemplate || "{}", "Request Template");
    const parameters = parseParameterDefinitions(definition.requestSchema || "[]");
    renderParameterDefinitions(parameters);
    (document.querySelector<HTMLTextAreaElement>("#request-example")!).value = JSON.stringify(requestTemplateFallback, null, 2);
    syncRequestContract();
    const responseDefinition = parseResponseDefinition(definition.responseSchema || '{"mode":"JSON"}');
    (document.querySelector<HTMLSelectElement>("#response-mode")!).value = responseDefinition.mode;
    (document.querySelector<HTMLInputElement>("#response-root")!).value = responseDefinition.root || "";
    (document.querySelector<HTMLInputElement>("#response-summary-root")!).value = responseDefinition.summaryRoot || "";
    (document.querySelector<HTMLTextAreaElement>("#response-example")!).value = "";
    renderResponseFields(responseDefinition.fields || []);
    renderResponseFields(responseDefinition.summaryFields || [], true);
    syncResponseContract();
    requestAdvancedDirty = false;
    responseAdvancedDirty = false;
    (document.querySelector<HTMLSelectElement>("#product-network")!).value = definition.network;
    (document.querySelector<HTMLInputElement>("#product-token")!).value = definition.tokenAddress;
    (document.querySelector<HTMLInputElement>("#product-price")!).value = formatUSDC(definition.priceUsdcAtomic);
    (document.querySelector<HTMLInputElement>("#product-cost")!).value = definition.providerInternalCostUsdcAtomic
      ? formatUSDC(definition.providerInternalCostUsdcAtomic)
      : "";
    (document.querySelector<HTMLInputElement>("#product-wallet")!).value = definition.receiverWallet;
    (document.querySelector<HTMLInputElement>("#notification-activation-url")!).value = definition.notification?.activationUrl || "";
    (document.querySelector<HTMLInputElement>("#notification-termination-url")!).value = definition.notification?.terminationUrl || "";
    (document.querySelector<HTMLInputElement>("#notification-included-count")!).value = String(definition.notification?.includedUsageCount || 100);
    (document.querySelector<HTMLInputElement>("#notification-unit-amount")!).value = formatUSDC(definition.notification?.usageUnitAmountAtomic || "10000");
    (document.querySelector<HTMLInputElement>("#notification-signing-secret")!).value = "";
    (document.querySelector<HTMLTextAreaElement>("#simulation-input")!).value = prettyJSON(definition.requestTemplate);
    lastSimulationResult = undefined;
    document.querySelector<HTMLElement>("#use-simulation-response")!.hidden = true;
    document.querySelector("#simulation-result")!.textContent = "尚未测试";
    document.querySelector("#editor-title")!.textContent = product.status === "DRAFT"
      ? "EDIT SERVICE"
      : working?.status === "PENDING_REVIEW"
        ? "VERSION UNDER REVIEW"
        : "EDIT NEXT VERSION";
    document.querySelector("#editor-version")!.textContent = working
      ? `${working.status} · V${working.version} · 线上 V${product.version}`
      : `${product.status} · V${product.version}`;
    document.querySelector<HTMLElement>("#test-panel")!.hidden = definition.productType === "NOTIFICATION";
    document.querySelector<HTMLElement>("#submit-review")!.hidden = !canSubmitProduct(product);
    document.querySelector<HTMLElement>("#provider-charge-confirm")!.hidden = definition.providerType !== "WINSHIELD";
    setDefinitionLocked(!canEditProduct(product));
    (document.querySelector<HTMLInputElement>("#product-slug")!).disabled = product.status === "PUBLISHED" || !canEditProduct(product);
    updateProductTypeFields();
    updateProviderFields();
    updateFeePreview();
    if (definition.providerType === "WINSHIELD") await loadBinding(product.id);
    setWizardStep(1);
    openProductDialog();
  }

  function prettyJSON(value: string): string {
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }

  function formatJSONField(id: string, kind: "array" | "object", label: string): void {
    const field = document.querySelector<HTMLTextAreaElement>(`#${id}`)!;
    try {
      field.value = JSON.stringify(parseJSON(field.value, kind, label), null, 2);
      field.setCustomValidity("");
    } catch (error) {
      field.setCustomValidity(error instanceof Error ? error.message : "JSON 无效");
      field.reportValidity();
    }
  }

  function productPayload(): Record<string, unknown> {
    syncRequestContract();
    syncResponseContract();
    const requestSchema = parseParameterDefinitions((document.querySelector<HTMLTextAreaElement>("#product-request-schema")!).value);
    const requestTemplate = parseJSONObject((document.querySelector<HTMLTextAreaElement>("#product-request-template")!).value, "Request Template");
    const responseSchema = parseResponseDefinition((document.querySelector<HTMLTextAreaElement>("#product-response-schema")!).value);
    const providerType = (document.querySelector<HTMLSelectElement>("#product-provider")!).value;
    const productType = (document.querySelector<HTMLSelectElement>("#product-type")!).value;
    const signingSecret = (document.querySelector<HTMLInputElement>("#notification-signing-secret")!).value;
    return {
      productType,
      name: (document.querySelector<HTMLInputElement>("#product-name")!).value.trim(),
      slug: (document.querySelector<HTMLInputElement>("#product-slug")!).value.trim(),
      description: (document.querySelector<HTMLTextAreaElement>("#product-description")!).value.trim(),
      serviceUrl: providerType === "GENERIC" ? (document.querySelector<HTMLInputElement>("#product-url")!).value.trim() : "",
      method: (document.querySelector<HTMLSelectElement>("#product-method")!).value,
      providerType,
      providerAction: (document.querySelector<HTMLInputElement>("#product-action")!).value.trim(),
      requestEncoding: (document.querySelector<HTMLSelectElement>("#product-request-encoding")!).value,
      requestSchema: JSON.stringify(requestSchema),
      requestTemplate: JSON.stringify(requestTemplate),
      responseSchema: JSON.stringify(responseSchema),
      paymentMode: "EXACT",
      network: (document.querySelector<HTMLSelectElement>("#product-network")!).value,
      tokenAddress: (document.querySelector<HTMLInputElement>("#product-token")!).value.trim(),
      priceUsdcAtomic: usdcToAtomic((document.querySelector<HTMLInputElement>("#product-price")!).value),
      providerInternalCostUsdcAtomic: (document.querySelector<HTMLInputElement>("#product-cost")!).value.trim()
        ? usdcToAtomic((document.querySelector<HTMLInputElement>("#product-cost")!).value)
        : "",
      receiverWallet: (document.querySelector<HTMLInputElement>("#product-wallet")!).value.trim(),
      notification: productType === "NOTIFICATION" ? {
        activationUrl: (document.querySelector<HTMLInputElement>("#notification-activation-url")!).value.trim(),
        terminationUrl: (document.querySelector<HTMLInputElement>("#notification-termination-url")!).value.trim(),
        includedUsageCount: Number((document.querySelector<HTMLInputElement>("#notification-included-count")!).value),
        usageUnitAmountAtomic: usdcToAtomic((document.querySelector<HTMLInputElement>("#notification-unit-amount")!).value),
        signingSecret,
      } : undefined,
    };
  }

  function updateProductTypeFields(): void {
    const notification = (document.querySelector<HTMLSelectElement>("#product-type")!).value === "NOTIFICATION";
    const product = products.find(item => item.id === (document.querySelector<HTMLInputElement>("#product-id")!).value);
    const hasSavedSecret = product ? editableDefinition(product).notification?.hasSigningSecret : false;
    document.querySelector<HTMLElement>("#notification-config")!.hidden = !notification;
    document.querySelector<HTMLElement>("#on-demand-interface")!.hidden = notification;
    for (const id of ["notification-activation-url", "notification-termination-url", "notification-included-count", "notification-unit-amount"]) {
      document.querySelector<HTMLInputElement>(`#${id}`)!.required = notification;
    }
    const secret = document.querySelector<HTMLInputElement>("#notification-signing-secret")!;
    secret.required = notification && !hasSavedSecret;
    document.querySelector("#notification-secret-help")!.textContent = hasSavedSecret
      ? "已配置签名密钥；留空保持不变，填写新值会轮换。"
      : "至少 16 位；平台加密保存且不回显。";
    document.querySelector("#product-price-label")!.textContent = notification ? "用户套餐售价（USDC）" : "用户单次售价（USDC）";
    document.querySelector("#product-cost-label")!.textContent = notification ? "套餐内部成本（USDC，可选）" : "内部成本（USDC，可选）";
    updateProviderFields();
  }

  function updateProviderFields(): void {
    if ((document.querySelector<HTMLSelectElement>("#product-type")!).value === "NOTIFICATION") {
      document.querySelector<HTMLInputElement>("#product-url")!.required = false;
      document.querySelector<HTMLInputElement>("#product-action")!.required = false;
      document.querySelector<HTMLElement>("#credential-section")!.hidden = true;
      document.querySelector<HTMLElement>("#provider-charge-confirm")!.hidden = true;
      return;
    }
    const isWintranx = (document.querySelector<HTMLSelectElement>("#product-provider")!).value === "WINSHIELD";
    document.querySelector<HTMLElement>("#generic-url-field")!.hidden = isWintranx;
    document.querySelector<HTMLInputElement>("#product-url")!.required = !isWintranx;
    document.querySelector<HTMLInputElement>("#product-action")!.required = isWintranx;
    if (isWintranx) {
      document.querySelector<HTMLSelectElement>("#product-method")!.value = "POST";
      document.querySelector<HTMLSelectElement>("#product-request-encoding")!.value = "JSON";
    }
    const id = (document.querySelector<HTMLInputElement>("#product-id")!).value;
    document.querySelector<HTMLElement>("#credential-section")!.hidden = !isWintranx || id === "";
    document.querySelector<HTMLElement>("#provider-charge-confirm")!.hidden = !isWintranx;
  }

  async function loadBinding(productId: string): Promise<void> {
    try {
      const body = await api<{ binding: ProductProviderBinding }>(`/v1/provider/products/${productId}/provider-binding`);
      currentBinding = body.binding;
      (document.querySelector<HTMLInputElement>("#credential-alias")!).value = body.binding.alias;
      (document.querySelector<HTMLInputElement>("#credential-endpoint")!).value = body.binding.endpointUrl;
      (document.querySelector<HTMLInputElement>("#credential-enabled")!).checked = body.binding.enabled;
      document.querySelector("#credential-state")!.textContent = `${body.binding.environment} · V${body.binding.version} · ${body.binding.lastTestStatus}`;
    } catch (error) {
      currentBinding = undefined;
      showStatus("credential-status", error instanceof Error ? error.message : "部署配置加载失败", true);
    }
  }

  async function saveBinding(): Promise<void> {
    const productId = (document.querySelector<HTMLInputElement>("#product-id")!).value;
    if (!productId) return;
    try {
      const body = await api<{ binding: ProductProviderBinding }>(`/v1/provider/products/${productId}/provider-binding`, {
        method: "PUT",
        body: JSON.stringify({
          environment: currentBinding?.environment || "DEV",
          alias: (document.querySelector<HTMLInputElement>("#credential-alias")!).value.trim(),
          endpointUrl: (document.querySelector<HTMLInputElement>("#credential-endpoint")!).value.trim(),
          enabled: (document.querySelector<HTMLInputElement>("#credential-enabled")!).checked,
          active: true,
          username: (document.querySelector<HTMLInputElement>("#credential-username")!).value,
          password: (document.querySelector<HTMLInputElement>("#credential-password")!).value,
          secret: (document.querySelector<HTMLInputElement>("#credential-secret")!).value,
        }),
      });
      currentBinding = body.binding;
      (document.querySelector<HTMLInputElement>("#credential-username")!).value = "";
      (document.querySelector<HTMLInputElement>("#credential-password")!).value = "";
      (document.querySelector<HTMLInputElement>("#credential-secret")!).value = "";
      document.querySelector("#credential-state")!.textContent = `${body.binding.environment} · V${body.binding.version} · ${body.binding.lastTestStatus}`;
      showStatus("credential-status", "部署配置已保存，凭据不会回显。");
    } catch (error) {
      showStatus("credential-status", error instanceof Error ? error.message : "部署配置保存失败", true);
    }
  }

  async function submitProduct(productId: string): Promise<void> {
    const product = products.find(item => item.id === productId);
    if (!product || !canSubmitProduct(product)) return;
    const definition = editableDefinition(product);
    const versionLabel = product.workingVersion ? ` V${product.workingVersion.version}` : "";
    const liveNote = product.status === "PUBLISHED" ? "，线上版本不受影响" : "";
    if (!window.confirm(`确认将“${definition.name}”${versionLabel}提交 Platform Admin 审核？提交后该版本将锁定${liveNote}。`)) return;
    try {
      await api(`/v1/provider/products/${productId}/submit`, { method: "POST" });
      await loadProducts();
      await editProduct(productId);
    } catch (error) {
      window.alert(error instanceof Error ? error.message : "提交审核失败");
    }
  }

  async function runSimulation(): Promise<void> {
    const productId = (document.querySelector<HTMLInputElement>("#product-id")!).value;
    const product = products.find(item => item.id === productId);
    if (!product) return;
    const definition = editableDefinition(product);
    let input: unknown;
    try {
      input = parseJSON((document.querySelector<HTMLTextAreaElement>("#simulation-input")!).value, "object", "测试请求");
    } catch (error) {
      showStatus("product-status", error instanceof Error ? error.message : "测试请求无效", true);
      return;
    }
    const confirmed = (document.querySelector<HTMLInputElement>("#confirm-provider-charge")!).checked;
    if (definition.providerType === "WINSHIELD" && !confirmed) {
      showStatus("product-status", "请先确认 Wintranx 测试可能产生 Provider 费用。", true);
      return;
    }
    const result = document.querySelector<HTMLElement>("#simulation-result")!;
    result.textContent = "调用中…";
    try {
      const body = await api<{ result: unknown }>(`/v1/provider/products/${productId}/simulate`, {
        method: "POST",
        headers: { "X-Confirm-Provider-Charge": confirmed ? "true" : "false" },
        body: JSON.stringify(input),
      });
      lastSimulationResult = body.result;
      result.textContent = JSON.stringify(body.result, null, 2);
      document.querySelector<HTMLElement>("#use-simulation-response")!.hidden = false;
      if (definition.providerType === "WINSHIELD") await loadBinding(productId);
    } catch (error) {
      lastSimulationResult = undefined;
      document.querySelector<HTMLElement>("#use-simulation-response")!.hidden = true;
      result.textContent = error instanceof Error ? error.message : "测试失败";
    }
  }

  function useSimulationResponse(): void {
    if (lastSimulationResult === undefined) return;
    document.querySelector<HTMLTextAreaElement>("#response-example")!.value =
      typeof lastSimulationResult === "string"
        ? lastSimulationResult
        : JSON.stringify(lastSimulationResult, null, 2);
    inferResponseDisplay();
    setWizardStep(3);
    showStatus("product-status", "已把测试结果带回返回展示，请确认字段后再次保存草稿。");
  }

  productForm.addEventListener("submit", async event => {
    event.preventDefault();
    const id = (document.querySelector<HTMLInputElement>("#product-id")!).value;
    const versioningPublishedProduct = products.find(product => product.id === id)?.status === "PUBLISHED";
    try {
      const body = await api<{ product: Product }>(id ? `/v1/provider/products/${id}` : "/v1/provider/products", {
        method: id ? "PUT" : "POST",
        body: JSON.stringify(productPayload()),
      });
      await loadProducts();
      await editProduct(body.product.id);
      setWizardStep(4);
      showStatus("product-status", id
        ? versioningPublishedProduct
          ? "版本草稿已保存；线上版本保持不变。"
          : "服务草稿已更新。"
        : "服务草稿已创建，可继续配置部署凭据和测试。");
    } catch (error) {
      showStatus("product-status", error instanceof Error ? error.message : "保存失败", true);
    }
  });

  document.querySelector("#new-product")!.addEventListener("click", () => {
    resetProductForm();
    openProductDialog();
  });
  document.querySelector("#close-product-dialog")!.addEventListener("click", closeProductDialog);
  document.querySelector("#wizard-back")!.addEventListener("click", () => setWizardStep(wizardStep - 1));
  document.querySelector("#wizard-next")!.addEventListener("click", () => {
    if (validateWizardStep()) setWizardStep(wizardStep + 1);
  });
  document.querySelector("#submit-review")!.addEventListener("click", () => {
    const id = (document.querySelector<HTMLInputElement>("#product-id")!).value;
    if (id) void submitProduct(id);
  });
  document.querySelector("#save-credential")!.addEventListener("click", () => void saveBinding());
  document.querySelector("#run-simulation")!.addEventListener("click", () => void runSimulation());
  document.querySelector("#use-simulation-response")!.addEventListener("click", useSimulationResponse);
  document.querySelector("#add-parameter")!.addEventListener("click", () => {
    const definitions = readParameterDefinitions();
    definitions.push({ name: "", label: "", type: "string", required: true });
    renderParameterDefinitions(definitions);
    syncRequestContract();
    parameterRows.querySelector<HTMLInputElement>("[data-parameter-row]:last-child [data-param=name]")?.focus();
  });
  document.querySelector("#infer-request")!.addEventListener("click", inferRequestParameters);
  document.querySelector("#apply-request-json")!.addEventListener("click", () => applyAdvancedRequestJSON());
  document.querySelector("#infer-response")!.addEventListener("click", inferResponseDisplay);
  document.querySelector("#apply-response-json")!.addEventListener("click", () => applyAdvancedResponseJSON());
  document.querySelector("#add-response-field")!.addEventListener("click", () => {
    const fields = readResponseFields(responseFieldRows, "[data-response-field-row]");
    fields.push({ path: "", label: "", format: "text" });
    renderResponseFields(fields);
    syncResponseContract();
    responseFieldRows.querySelector<HTMLInputElement>("[data-response-field-row]:last-child [data-response=path]")?.focus();
  });
  document.querySelector("#add-response-summary-field")!.addEventListener("click", () => {
    const fields = readResponseFields(responseSummaryFieldRows, "[data-response-summary-field-row]");
    fields.push({ path: "", label: "", format: "text" });
    renderResponseFields(fields, true);
    syncResponseContract();
    responseSummaryFieldRows.querySelector<HTMLInputElement>("[data-response-summary-field-row]:last-child [data-response=path]")?.focus();
  });
  document.querySelector("#format-request-schema")!.addEventListener("click", () => formatJSONField("product-request-schema", "array", "Request Schema"));
  document.querySelector("#format-response-schema")!.addEventListener("click", () => formatJSONField("product-response-schema", "object", "Response Schema"));
  document.querySelector<HTMLTextAreaElement>("#product-request-schema")!.addEventListener("input", () => { requestAdvancedDirty = true; });
  document.querySelector<HTMLTextAreaElement>("#product-request-template")!.addEventListener("input", () => { requestAdvancedDirty = true; });
  document.querySelector<HTMLTextAreaElement>("#product-response-schema")!.addEventListener("input", () => { responseAdvancedDirty = true; });
  parameterRows.addEventListener("input", syncRequestContract);
  parameterRows.addEventListener("change", event => {
    const row = (event.target as HTMLElement).closest<HTMLElement>("[data-parameter-row]");
    if (row) updateParameterCard(row);
    syncRequestContract();
  });
  responseFieldRows.addEventListener("input", syncResponseContract);
  responseFieldRows.addEventListener("change", syncResponseContract);
  responseSummaryFieldRows.addEventListener("input", syncResponseContract);
  responseSummaryFieldRows.addEventListener("change", syncResponseContract);
  document.querySelector("#response-mode")!.addEventListener("change", syncResponseContract);
  document.querySelector("#response-root")!.addEventListener("input", syncResponseContract);
  document.querySelector("#response-summary-root")!.addEventListener("input", syncResponseContract);
  document.querySelector("#response-example")!.addEventListener("input", renderResponsePreview);
  document.querySelector("#product-provider")!.addEventListener("change", updateProviderFields);
  document.querySelector("#product-method")!.addEventListener("change", () => {
    if (document.querySelector<HTMLSelectElement>("#product-method")!.value === "GET") {
      document.querySelector<HTMLSelectElement>("#product-request-encoding")!.value = "QUERY";
    }
  });
  document.querySelector("#product-request-encoding")!.addEventListener("change", () => {
    if (document.querySelector<HTMLSelectElement>("#product-request-encoding")!.value === "MULTIPART") {
      document.querySelector<HTMLSelectElement>("#product-method")!.value = "POST";
    }
  });
  document.querySelector("#product-type")!.addEventListener("change", updateProductTypeFields);
  productDialog.addEventListener("click", event => {
    if (event.target === productDialog) closeProductDialog();
  });

  resetProductForm();
  document.querySelector("#product-price")!.addEventListener("input", updateFeePreview);
  document.querySelector("#product-cost")!.addEventListener("input", updateFeePreview);

  return {
    loadProducts,
    setFeeRule: rule => {
      feeRule = rule;
      updateFeePreview();
      renderProducts();
    },
  };
}
